import { useTranslation } from 'react-i18next';

import { ColorName } from 'types';
import { OrderStats } from 'types/api/orders.type';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { formatCount, formatPrice } from 'helpers/parsePrice';

import { Headline } from 'components/Headline';

import * as S from './OrderStatsTiles.styles';

type OrderStatsTilesProps = {
  title: string;
  data?: OrderStats;
  isLoading: boolean;
  /** Accent colour of the tiles (defaults to colmarBlue). */
  headerColor?: ColorName;
  /**
   * Opt-in to the extended KPI set (Units / Avg unit price / Clients). Default
   * false keeps the four-tile layout byte-identical for existing callers (the
   * Orders page). The extra tiles only appear when the backend actually
   * returned the new fields, so this renders correctly against an old backend.
   */
  extended?: boolean;
};

/**
 * One row of KPI tiles for a single stats scope. In the default (four-tile)
 * shape — Orders / Total value / Avg order value / Acceptance rate — it is
 * shared between the Orders list page ("My performance", tinted by the selected
 * warehouse) and the Statistics page. With `extended`, and only when the
 * backend supplied the new fields, it appends three more tiles (Units / Avg
 * unit price / Clients) and switches to the wider grid. Money renders an em
 * dash when the BE returned null and appends the mixed-currencies hint when
 * more than one currency is in scope.
 */
export function OrderStatsTiles({
  title,
  data,
  isLoading,
  headerColor,
  extended = false,
}: Readonly<OrderStatsTilesProps>): React.ReactElement {
  const { t } = useTranslation();
  const priceVisible = usePriceVisible();
  const currency = data?.currency ?? '';
  const isMixedCurrency = !!data?.byCurrency && Object.keys(data.byCurrency).length > 1;
  // The money/units/clients figures exclude rejected orders while the order
  // COUNT includes them (acceptanceRate needs them) — so say so, but only when
  // the scope actually holds a rejected order and the difference is real.
  const hasRejected = (data?.byStatus?.REJECTED ?? 0) > 0;

  /**
   * Hints are additive; join them so a tile can carry more than one. Takes the
   * loose `t()` return type (it can be null when a key is missing) and keeps
   * only real strings.
   */
  const hintOf = (...parts: unknown[]): string | undefined => {
    const kept = parts.filter((p): p is string => typeof p === 'string' && p.length > 0);
    return kept.length ? kept.join(' · ') : undefined;
  };

  const money = (value: number | null | undefined): string =>
    value === null || value === undefined ? '—' : formatPrice(currency, value);

  const percent = (ratio: number | undefined): string =>
    ratio === undefined ? '—' : `${Math.round(ratio * 100)}%`;

  // Show the extra tiles only when opted in AND the backend returned the new
  // fields (all three present). Against an old backend they are undefined, so
  // the tiles — and the wider grid — stay hidden.
  const showExtended =
    extended &&
    data?.totalUnits !== undefined &&
    data?.totalClients !== undefined &&
    data?.averageUnitPrice !== undefined;

  const tiles: { id: string; label: string; value: string; hint?: string }[] = [
    {
      id: 'orders',
      label: t('order.label_other'),
      value: data ? String(data.totalOrders) : '—',
    },
    ...(priceVisible
      ? [
          {
            id: 'totalValue',
            label: t('statistics.totalValue'),
            value: money(data?.totalValue),
            hint: hintOf(
              isMixedCurrency && t('statistics.mixedCurrencies'),
              hasRejected && t('statistics.exclRejected')
            ),
          },
          {
            id: 'averageOrderValue',
            label: t('statistics.avgOrderValue'),
            value: money(data?.averageOrderValue),
            hint: hintOf(hasRejected && t('statistics.exclRejected')),
          },
        ]
      : []),
    {
      id: 'acceptanceRate',
      label: t('statistics.acceptanceRate'),
      value: percent(data?.acceptanceRate),
    },
  ];

  if (showExtended && data) {
    tiles.push(
      {
        id: 'units',
        label: t('statistics.units'),
        value: formatCount(data.totalUnits),
        hint: hintOf(hasRejected && t('statistics.exclRejected')),
      },
      ...(priceVisible
        ? [
            {
              id: 'averageUnitPrice',
              label: t('statistics.avgUnitPrice'),
              value: money(data.averageUnitPrice),
              hint: hintOf(
                isMixedCurrency && t('statistics.mixedCurrencies'),
                hasRejected && t('statistics.exclRejected')
              ),
            },
          ]
        : []),
      {
        id: 'clients',
        label: t('statistics.clients'),
        value: formatCount(data.totalClients),
        hint: hintOf(hasRejected && t('statistics.exclRejected')),
      }
    );
  }

  return (
    <S.Row>
      <Headline
        tag="h3"
        scale="base"
        color="grayDark"
        fontWeight="bold"
        textTransform="isFirstLetterUppercase"
      >
        {title}
      </Headline>

      <S.Tiles $extended={showExtended}>
        {tiles.map((tile) => (
          <S.Tile key={tile.id} aria-busy={isLoading} $headerColor={headerColor}>
            <S.Value $headerColor={headerColor}>{isLoading ? '…' : tile.value}</S.Value>
            <S.Label>{tile.hint ? `${tile.label} (${tile.hint})` : tile.label}</S.Label>
          </S.Tile>
        ))}
      </S.Tiles>
    </S.Row>
  );
}
